import React, { useEffect, useState } from 'react';
import { Check, Loader2, Minus, Plus } from 'lucide-react';
import tokenStore from '../../utils/tokenStore';
import { API_URL } from '../../constants/api';

const API = `${API_URL}/api`;

const PLAN_ORDER = ['free', 'solo', 'team'];

const PricingPage = ({ onBack, currentUser }) => {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // null until the catalog loads — the Team floor (seat_min) is server-driven,
  // so seeding this with a literal would briefly show an invalid seat count.
  const [teamSeats, setTeamSeats] = useState(null);

  useEffect(() => {
    const token = tokenStore.get();
    const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    Promise.all([
      fetch(`${API}/billing/plans`, { headers }).then(r => r.json()),
      fetch(`${API}/billing/subscription`, { headers }).then(r => r.json()),
    ])
      .then(([planList, sub]) => {
        const sorted = [...planList].sort((a, b) => PLAN_ORDER.indexOf(a.id) - PLAN_ORDER.indexOf(b.id));
        setPlans(sorted);
        setSubscription(sub);
        // Start the seat stepper at what's actually billed, so a Team
        // subscriber sees their real seat count rather than the plan floor.
        // Clamp up to seat_min: a legacy 1-seat Team predates the 2-seat
        // minimum and would otherwise render a quantity checkout now rejects.
        const teamPlan = sorted.find(p => p.per_seat);
        const floor = teamPlan?.seat_min ?? 1;
        setTeamSeats(
          sub?.plan === 'team' && sub.seats_purchased > 0
            ? Math.max(floor, sub.seats_purchased)
            : floor,
        );
      })
      .catch(() => setError('Could not load pricing — please try again later'))
      .finally(() => setLoading(false));
  }, []);

  const handleSubscribe = async (planId, seatCount) => {
    setCheckoutLoadingPlan(planId);
    setError('');
    setNotice('');
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API}/billing/checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ plan_id: planId, ...(seatCount ? { seats: seatCount } : {}) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not start checkout');
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      // No URL means the backend switched an existing subscription in place —
      // there's nothing to redirect to, so reflect the new plan right here.
      setNotice(data.message || 'Your plan has been updated.');
      setSubscription(prev => (prev ? {
        ...prev,
        plan: planId,
        plan_status: 'active',
        seats_purchased: seatCount || 1,
      } : prev));
      setCheckoutLoadingPlan(null);
    } catch (err) {
      setError(err.message);
      setCheckoutLoadingPlan(null);
    }
  };

  const billingEnabled = subscription?.billing_enabled ?? false;
  // On a paid plan already — plan changes go through the existing subscription
  // rather than a fresh checkout, so the CTAs read "Switch to …".
  const isSubscribed = !!subscription && subscription.plan !== 'free';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center py-10 px-4" style={{ backgroundColor: 'var(--bg-base)' }}>
      <div className="w-full max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="mb-6 flex items-center gap-2 text-sm font-medium transition-colors"
          style={{ color: 'var(--accent-primary)' }}
        >
          <span>←</span> Back
        </button>

        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Plans &amp; Pricing</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Solo plans for individual founders, Team plans for co-founders and crews.
          </p>
        </div>

        {!billingEnabled && !loading && (
          <div
            className="mb-8 mx-auto max-w-2xl p-3 rounded-lg text-center text-[13px] sm:text-sm"
            style={{ backgroundColor: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', color: '#f59e0b' }}
          >
            Checkout isn't enabled yet — plans are shown for preview while billing is being set up.
          </div>
        )}

        {error && (
          <div
            className="mb-8 mx-auto max-w-2xl p-3 rounded-lg text-center text-[13px] sm:text-sm"
            style={{ backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444' }}
          >
            {error}
          </div>
        )}

        {notice && (
          <div
            className="mb-8 mx-auto max-w-2xl p-3 rounded-lg text-center text-[13px] sm:text-sm"
            style={{ backgroundColor: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22c55e' }}
          >
            {notice}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 justify-center">
            {plans.map((plan) => {
              const isCurrent = subscription?.plan === plan.id;
              const isFree = plan.id === 'free';
              const isTeam = plan.per_seat;
              const loadingThis = checkoutLoadingPlan === plan.id;
              const seatCount = teamSeats ?? plan.seat_min ?? 1;
              const displayPriceCents = isTeam ? plan.price_cents * seatCount : plan.price_cents;
              const symbol = plan.currency_symbol ?? '€';
              const seatFloor = plan.seat_min ?? 1;

              // Staying on the current Team plan but changing the seat count is
              // still a real change — the backend updates the subscription
              // quantity in place — so the button stays live for it.
              const seatsChanged = isCurrent && isTeam && seatCount !== (subscription?.seats_purchased ?? 1);
              const actionable = billingEnabled && !isFree && (!isCurrent || seatsChanged);
              const ctaLabel = isFree
                ? 'Included'
                : seatsChanged
                  ? `Update to ${seatCount} seat${seatCount !== 1 ? 's' : ''}`
                  : isCurrent
                    ? 'Current plan'
                    : isSubscribed
                      ? `Switch to ${plan.name}`
                      : 'Subscribe';

              return (
                <div
                  key={plan.id}
                  className="rounded-2xl p-5 flex flex-col"
                  style={{
                    backgroundColor: 'var(--card-bg-solid)',
                    border: isCurrent ? '1px solid var(--accent-primary)' : '1px solid var(--card-border)',
                    boxShadow: isCurrent ? '0 0 0 1px var(--accent-primary)' : 'none',
                  }}
                >
                  {isTeam && (
                    <span
                      className="self-start mb-2 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide"
                      style={{ backgroundColor: 'rgba(52,211,153,0.12)', color: '#34d399' }}
                    >
                      Team
                    </span>
                  )}
                  <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{plan.name}</h3>
                  <div className="mt-2 mb-1 flex items-baseline gap-1.5">
                    <span className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                      {displayPriceCents === 0 ? 'Free' : `${symbol}${(displayPriceCents / 100).toFixed(0)}`}
                    </span>
                    {plan.price_cents > 0 && (
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>/{plan.interval}</span>
                    )}
                  </div>
                  {isTeam && (
                    <>
                      <p className="text-[11px] mb-2" style={{ color: 'var(--text-muted)' }}>
                        {symbol}{(plan.price_cents / 100).toFixed(0)}/seat/month
                      </p>
                      <div className="flex items-center gap-2 mb-4">
                        <button
                          type="button"
                          onClick={() => setTeamSeats(s => Math.max(seatFloor, (s ?? seatFloor) - 1))}
                          disabled={seatCount <= seatFloor}
                          className="w-7 h-7 rounded-lg flex items-center justify-center disabled:opacity-40"
                          style={{ border: '1px solid var(--card-border)', color: 'var(--text-secondary)' }}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-sm font-medium w-16 text-center" style={{ color: 'var(--text-primary)' }}>
                          {seatCount} seat{seatCount !== 1 ? 's' : ''}
                        </span>
                        <button
                          type="button"
                          onClick={() => setTeamSeats(s => Math.min(plan.seat_limit, (s ?? seatFloor) + 1))}
                          disabled={seatCount >= plan.seat_limit}
                          className="w-7 h-7 rounded-lg flex items-center justify-center disabled:opacity-40"
                          style={{ border: '1px solid var(--card-border)', color: 'var(--text-secondary)' }}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </>
                  )}
                  {!isTeam && <div className="mb-4" />}
                  <ul className="space-y-2 mb-5 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                        <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent-primary)' }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {isCurrent && subscription?.ai_messages_limit != null && (
                    <p className="text-[11px] mb-3" style={{ color: 'var(--text-muted)' }}>
                      {subscription.ai_messages_remaining} of {subscription.ai_messages_limit} AI
                      messages left today
                    </p>
                  )}
                  <button
                    onClick={() => actionable && handleSubscribe(plan.id, isTeam ? seatCount : undefined)}
                    disabled={!actionable || loadingThis}
                    className="w-full px-4 py-2.5 rounded-lg text-[13px] font-medium transition-opacity disabled:opacity-50"
                    style={{
                      backgroundColor: actionable ? 'var(--accent-primary)' : 'var(--interactive-default)',
                      color: actionable ? '#fff' : 'var(--text-muted)',
                      cursor: actionable ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {loadingThis ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        {isSubscribed ? 'Updating…' : 'Redirecting…'}
                      </span>
                    ) : ctaLabel}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingPage;
